import { useEffect, useState } from 'react';
import { request, ApiError } from '@/lib/api.js';
import { GlassButton, PageHeader, Section } from '@/components/ui/index.js';
import type { PricingResponse } from '@/lib/types.js';

/**
 * 闲鱼买家开账号
 *
 * 两种模式 tab：
 *  - 邮箱模式：admin 输入买家邮箱 → 后端建账号 + grant 会员 + Resend 发欢迎邮件
 *  - 账号模式：后端自动生成 shark{6 数字} 用户名 + 12 位密码，返回明文给 admin 一次性显示
 *
 * 提交后会在一张「凭据卡」上展示账号 / 密码 / 套餐 / 到期日，每个字段右侧带复制按钮，
 * admin 一键复制给闲鱼买家即可。
 */

type Mode = 'email' | 'username';
type Plan = 'monthly' | 'yearly';

interface OpenResult {
  ok: true;
  user_id: string;
  mode: Mode;
  email: string;
  username: string | null;
  temp_password: string;
  plan: Plan;
  expires_at: string;
  email_sent: boolean;
}

export function XianyuOpenPage(): JSX.Element {
  const [mode, setMode] = useState<Mode>('email');
  const [email, setEmail] = useState('');
  const [plan, setPlan] = useState<Plan>('monthly');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<OpenResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 套餐价从后台拉 live 值(价格可在定价页改), 别写死。拉失败回退占位价。
  const [prices, setPrices] = useState<{ monthly: number; yearly: number } | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await request<PricingResponse>('/admin/pricing');
        setPrices({ monthly: res.monthly?.cny ?? 79, yearly: res.yearly?.cny ?? 699 });
      } catch (err) {
        console.warn('[xianyu] 拉取定价失败, 用占位价:', err);
      }
    })();
  }, []);

  function switchMode(next: Mode): void {
    setMode(next);
    setResult(null);
    setError(null);
  }

  async function submit(): Promise<void> {
    setError(null);
    setResult(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        mode,
        plan,
      };
      if (mode === 'email') {
        if (!email.trim()) {
          setError('请填写买家邮箱');
          setSubmitting(false);
          return;
        }
        body['email'] = email.trim().toLowerCase();
      }
      const res = await request<OpenResult>('/admin/users/create_for_xianyu', {
        method: 'POST',
        body,
      });
      setResult(res);
      // Reset form for next buyer
      if (mode === 'email') setEmail('');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.fields?.['email'] === 'already_exists') {
          setError('该邮箱已注册，无法重复开户');
        } else {
          setError(err.message);
        }
      } else {
        setError('网络错误，请稍后重试');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader
        title="闲鱼买家开账号"
        subtitle={
          <>
            手动为闲鱼买家开通会员。买家说留邮箱选 <strong>邮箱模式</strong>，
            买家不会用邮箱选 <strong>账号模式</strong>。
          </>
        }
      />

      {/* Mode tabs */}
      <div className="flex gap-1 border-b border-white/[0.08] mb-6">
        {(['email', 'username'] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={(): void => switchMode(m)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              mode === m
                ? 'border-shark-500 text-shark-200'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            {m === 'email' ? '邮箱模式（发欢迎邮件）' : '账号模式（自动生成账号密码）'}
          </button>
        ))}
      </div>

      {/* Form */}
      <Section size="md" className="mb-6">
        <div className="space-y-6">
          {mode === 'email' && (
            <div>
              <label htmlFor="xy-email" className="block text-sm font-medium text-slate-300 mb-2">
                买家邮箱
              </label>
              <input
                type="email"
                id="xy-email"
                value={email}
                onChange={(e): void => setEmail(e.target.value)}
                placeholder="buyer@example.com"
                className="w-full h-11 px-3.5 bg-white/[0.03] border border-white/10 rounded-lg text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-shark-500 focus:ring-1 focus:ring-shark-500/40 transition-colors"
              />
              <p className="mt-2 text-xs text-slate-500">
                开通后系统会发邮件到这个地址，里面有登录链接 + 临时密码。
              </p>
            </div>
          )}

          {mode === 'username' && (
            <div className="rounded-lg bg-white/[0.03] border border-white/10 px-3.5 py-3 text-xs text-slate-400">
              后端自动生成 <code className="text-amber-300">shark + 6 数字</code> 用户名 +
              12 位随机临时密码。提交后会一次性显示，<strong>记得马上复制发给买家</strong>。
            </div>
          )}

          <div>
            <span className="block text-sm font-medium text-slate-300 mb-2">套餐</span>
            <div className="flex gap-2">
              {(['monthly', 'yearly'] as Plan[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={(): void => setPlan(p)}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg border transition-all ${
                    plan === p
                      ? 'border-shark-500/60 bg-shark-500/20 text-shark-200 shadow-inner shadow-shark-500/10'
                      : 'border-white/10 text-slate-300 hover:border-white/10 hover:bg-white/[0.04]'
                  }`}
                >
                  {p === 'monthly'
                    ? `月卡（¥${prices?.monthly ?? 79}）`
                    : `年卡（¥${prices?.yearly ?? 699}）`}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              月卡 = 自然 1 个月（5/21 开 → 6/21 到期），年卡同理。
            </p>
          </div>

          {error !== null && (
            <div className="rounded-lg bg-rose-900/30 border border-rose-700/50 px-3.5 py-2.5 text-sm text-rose-300">
              {error}
            </div>
          )}

          <div className="pt-2">
            <GlassButton
              onClick={(): void => void submit()}
              disabled={submitting}
              variant="primary"
              size="lg"
              className="w-full"
            >
              {submitting ? '开通中…' : '开通账号'}
            </GlassButton>
          </div>
        </div>
      </Section>

      {result !== null && <ResultCard result={result} />}
    </div>
  );
}

function ResultCard({ result }: { result: OpenResult }): JSX.Element {
  const isEmailMode = result.mode === 'email';
  const expiresPretty = result.expires_at.slice(0, 10);
  const [bundleCopied, setBundleCopied] = useState(false);

  // 闲鱼私信用的整段凭据，admin 一键复制贴给买家。
  const bundle = buildCredentialBundle(result, expiresPretty);

  async function copyBundle(): Promise<void> {
    try {
      await navigator.clipboard.writeText(bundle);
      setBundleCopied(true);
      setTimeout(() => setBundleCopied(false), 2000);
    } catch {
      // 现代浏览器都支持，忽略。
    }
  }

  return (
    <div className="rounded-xl border-2 border-emerald-500/50 bg-emerald-900/20 p-5 space-y-4 shadow-xl shadow-emerald-900/30">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-emerald-200">✅ 开通成功</h2>
        {isEmailMode && (
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${
              result.email_sent
                ? 'bg-emerald-700/50 text-emerald-200'
                : 'bg-amber-700/40 text-amber-200'
            }`}
          >
            {result.email_sent ? '✉️ 欢迎邮件已发送' : '⚠️ 邮件发送失败，手动转给买家'}
          </span>
        )}
      </div>

      <div className="rounded-lg border border-amber-500/40 bg-amber-900/20 px-3 py-2 text-xs text-amber-200">
        ⚠️ 临时密码<strong>仅本次显示</strong>。刷新页面 / 关闭后<strong>无法再找回</strong>，
        请立即复制发给买家。
      </div>

      <div className="space-y-2 text-sm">
        {result.username !== null && (
          <CredRow label="账号" value={result.username} mono />
        )}
        <CredRow
          label={isEmailMode ? '邮箱' : '邮箱（合成 · 仅内部用）'}
          value={result.email}
          mono
        />
        <CredRow label="临时密码" value={result.temp_password} mono highlight />
        <CredRow
          label="套餐"
          value={result.plan === 'monthly' ? '月卡（1 个月会员）' : '年卡（1 年会员）'}
        />
        <CredRow label="到期日" value={expiresPretty} />
      </div>

      {/* 一键复制全部 — 最常用的操作前置 */}
      <GlassButton
        onClick={(): void => void copyBundle()}
        variant="primary"
        size="lg"
        className="w-full"
      >
        {bundleCopied ? '✓ 已复制整段，去闲鱼私信粘贴' : '📋 一键复制整段凭据'}
      </GlassButton>

      <div className="pt-2 border-t border-emerald-500/20 text-xs text-emerald-200/80 leading-relaxed">
        {isEmailMode ? (
          <>提醒买家检查邮箱（含垃圾邮件文件夹）。若反馈没收到，点上面的临时密码复制后通过闲鱼私信补发。</>
        ) : (
          <>把账号 + 密码 + 到期日通过闲鱼私信发给买家。买家忘密码时回到该用户的详情页用「重置密码」按钮生成新的即可。</>
        )}
      </div>
    </div>
  );
}

function buildCredentialBundle(result: OpenResult, expiresPretty: string): string {
  const lines = ['【小鲨鱼会员账号】'];
  if (result.username !== null) lines.push(`账号：${result.username}`);
  lines.push(`邮箱：${result.email}`);
  lines.push(`临时密码：${result.temp_password}`);
  lines.push(`套餐：${result.plan === 'monthly' ? '月卡（1 个月）' : '年卡（1 年）'}`);
  lines.push(`到期：${expiresPretty}`);
  lines.push('');
  lines.push('登录入口：https://littleshark.app/app/login');
  lines.push('（首次登录后建议在【账户】里改密码）');
  return lines.join('\n');
}

function CredRow({
  label,
  value,
  mono,
  highlight,
}: {
  label: string;
  value: string;
  mono?: boolean;
  highlight?: boolean;
}): JSX.Element {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fall back to legacy textarea trick if needed; navigator.clipboard
      // is fine on admin's modern browsers.
    }
  }

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-xs text-slate-400">{label}</div>
        <div
          className={`truncate ${mono === true ? 'font-mono' : ''} ${
            highlight === true ? 'text-amber-200 text-base' : 'text-slate-100'
          }`}
        >
          {value}
        </div>
      </div>
      <div className="shrink-0">
        <GlassButton size="sm" variant="ghost" onClick={(): void => void copy()}>
          {copied ? '已复制' : '复制'}
        </GlassButton>
      </div>
    </div>
  );
}
